import { useState } from 'react';
import { useTheme } from '@/context/ThemeContext';

const industries = [
  {
    icon: 'ri-scales-3-line',
    name: 'Legal Services',
    descriptor: 'Law Firms & Legal Practices',
    description:
      'Capture every potential client inquiry and ensure immediate response without administrative delays. Our systems handle intake qualification, appointment scheduling, and follow-up — so your attorneys focus on cases, not coordination.',
    outcomes: ['Zero missed client inquiries', 'Automated intake qualification', 'Instant response to all leads'],
    image: 'https://public.readdy.ai/ai/img_res/15dee2985ec7ff869f45e6d442990fd6.jpg',
  },
  {
    icon: 'ri-line-chart-line',
    name: 'Brokerage & Capital Markets',
    descriptor: 'Financial Services & Trading',
    description:
      'Maintain speed and precision in client interactions where timing is critical. Our systems ensure no opportunity is delayed, every client communication is tracked, and your team operates with complete informational clarity.',
    outcomes: ['Real-time client communication', 'Precision lead tracking', 'Compliance-ready documentation'],
    image: 'https://readdy.ai/api/search-image?query=dark%20financial%20trading%20environment%20with%20multiple%20screens%20showing%20charts%20and%20data%2C%20sophisticated%20brokerage%20office%2C%20teal%20and%20navy%20blue%20tones%2C%20minimalist%20professional%20setting%20with%20cool%20lighting&width=800&height=500&seq=ind-finance-teal-001&orientation=landscape',
  },
  {
    icon: 'ri-tools-line',
    name: 'Trade Services',
    descriptor: 'Contractors & Field Operations',
    description:
      'Book jobs while your team is on-site, ensuring no opportunity is missed. Our systems capture inbound requests, qualify leads, and schedule appointments automatically — even when your crew is in the field.',
    outcomes: ['On-site job booking automation', 'After-hours inquiry capture', 'Automated estimate follow-up'],
    image: 'https://readdy.ai/api/search-image?query=professional%20trade%20services%20workshop%20with%20tools%20and%20equipment%2C%20dark%20navy%20industrial%20aesthetic%2C%20cool%20teal%20accent%20lighting%2C%20skilled%20craftsman%20environment%2C%20minimalist%20dark%20blue%20tones&width=800&height=500&seq=ind-trade-teal-001&orientation=landscape',
  },
  {
    icon: 'ri-heart-pulse-line',
    name: 'Clinical Practices',
    descriptor: 'Medical & Healthcare Providers',
    description:
      'Maintain consistent patient flow without overwhelming front desk operations. Our systems handle appointment scheduling, patient intake, and follow-up communications — reducing administrative burden while improving patient experience.',
    outcomes: ['Automated patient scheduling', 'Intake form automation', 'Recall & follow-up systems'],
    image: 'https://readdy.ai/api/search-image?query=modern%20clinical%20medical%20practice%20interior%20with%20clean%20minimalist%20design%2C%20dark%20navy%20sophisticated%20healthcare%20environment%2C%20cool%20teal%20accent%20lighting%2C%20professional%20medical%20office%20with%20blue%20tones&width=800&height=500&seq=ind-clinical-teal-001&orientation=landscape',
  },
  {
    icon: 'ri-sparkling-line',
    name: 'Med Spas',
    descriptor: 'Aesthetic & Wellness Centers',
    description:
      'Maintain consistent client flow and maximize booking capacity. Our systems capture new client inquiries, automate appointment reminders, and manage follow-up sequences that drive repeat visits and referrals.',
    outcomes: ['Booking capacity optimization', 'Automated client reminders', 'Referral & retention systems'],
    image: 'https://readdy.ai/api/search-image?query=luxurious%20modern%20med%20spa%20interior%20with%20elegant%20minimalist%20design%2C%20sophisticated%20wellness%20environment%2C%20cool%20teal%20and%20navy%20blue%20accent%20lighting%2C%20premium%20aesthetic%20clinic%20atmosphere%20with%20deep%20blue%20tones&width=800&height=500&seq=ind-medspa-teal-001&orientation=landscape',
  },
];

export default function Industries() {
  const [active, setActive] = useState(0);
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <section id="industries" className={`py-28 px-6 md:px-10 transition-colors duration-500 ${
      isDark ? 'bg-[#0b1f3a]' : 'bg-[#e8f4f6]'
    }`}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-16">
          <span className={`text-xs font-medium tracking-widest uppercase mb-4 block ${
            isDark ? 'text-[#14b8c8]' : 'text-[#0d7a8a]'
          }`}>
            PRECISION DEPLOYMENT
          </span>
          <h2 className={`text-4xl md:text-6xl font-thin leading-tight ${
            isDark ? 'text-white/70' : 'text-[#0b1f3a]/70'
          }`}>
            Built for Industries
          </h2>
          <h3 className={`text-4xl md:text-6xl font-bold leading-tight ${
            isDark ? 'text-white' : 'text-[#0b1f3a]'
          }`}>
            Where Performance<br />
            <span className={isDark ? 'text-[#14b8c8]' : 'text-[#0d7a8a]'}>Is Non-Negotiable</span>
          </h3>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Industry List */}
          <div className="flex flex-col gap-3 lg:w-[420px] shrink-0">
            {industries.map((ind, i) => (
              <button
                key={ind.name}
                onClick={() => setActive(i)}
                className={`flex items-center gap-4 px-6 py-5 rounded-xl border text-left transition-all duration-200 cursor-pointer ${
                  active === i
                    ? isDark
                      ? 'bg-[#14b8c8]/10 border-[#14b8c8]/40 border-l-4 border-l-[#14b8c8]'
                      : 'bg-[#0d7a8a]/10 border-[#0d7a8a]/40 border-l-4 border-l-[#0d7a8a]'
                    : isDark
                      ? 'bg-white/5 border-white/10 hover:border-white/20 hover:bg-white/8'
                      : 'bg-white/50 border-slate-200 hover:border-[#0d7a8a]/30 hover:bg-white/80'
                }`}
              >
                <div className={`w-10 h-10 flex items-center justify-center rounded-lg ${
                  active === i
                    ? isDark ? 'bg-[#14b8c8]/20' : 'bg-[#0d7a8a]/20'
                    : isDark ? 'bg-white/5' : 'bg-slate-100'
                }`}>
                  <i className={`${ind.icon} text-xl ${
                    active === i
                      ? isDark ? 'text-[#14b8c8]' : 'text-[#0d7a8a]'
                      : isDark ? 'text-white/40' : 'text-slate-400'
                  }`}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <div className={`font-semibold text-base ${
                    active === i
                      ? isDark ? 'text-white' : 'text-[#0b1f3a]'
                      : isDark ? 'text-white/70' : 'text-slate-600'
                  }`}>{ind.name}</div>
                  <div className={`text-xs mt-0.5 ${isDark ? 'text-white/40' : 'text-slate-400'}`}>{ind.descriptor}</div>
                </div>
                <div className={`w-5 h-5 flex items-center justify-center ${
                  active === i
                    ? isDark ? 'text-[#14b8c8]' : 'text-[#0d7a8a]'
                    : isDark ? 'text-white/20' : 'text-slate-300'
                }`}>
                  <i className="ri-arrow-right-s-line text-lg"></i>
                </div>
              </button>
            ))}
          </div>

          {/* Detail Panel */}
          <div className={`flex-1 rounded-2xl overflow-hidden border ${
            isDark
              ? 'bg-[#0d2847] border-white/10'
              : 'bg-white border-slate-200'
          }`}>
            <div className="w-full h-56 md:h-72 overflow-hidden">
              <img
                src={industries[active].image}
                alt={industries[active].name}
                className="w-full h-full object-cover object-top transition-all duration-500"
              />
            </div>
            <div className="p-8">
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-10 h-10 flex items-center justify-center rounded-lg ${
                  isDark ? 'bg-[#14b8c8]/15' : 'bg-[#0d7a8a]/15'
                }`}>
                  <i className={`${industries[active].icon} text-xl ${
                    isDark ? 'text-[#14b8c8]' : 'text-[#0d7a8a]'
                  }`}></i>
                </div>
                <div>
                  <h4 className={`font-bold text-xl ${isDark ? 'text-white' : 'text-[#0b1f3a]'}`}>{industries[active].name}</h4>
                  <p className={`text-sm ${isDark ? 'text-white/40' : 'text-slate-400'}`}>{industries[active].descriptor}</p>
                </div>
              </div>
              <p className={`text-base leading-relaxed mb-6 ${
                isDark ? 'text-white/60' : 'text-slate-600'
              }`}>{industries[active].description}</p>
              <div className="flex flex-col gap-3">
                {industries[active].outcomes.map((o) => (
                  <div key={o} className="flex items-center gap-3">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className={`ri-checkbox-circle-line text-base ${
                        isDark ? 'text-[#14b8c8]' : 'text-[#0d7a8a]'
                      }`}></i>
                    </div>
                    <span className={`text-sm ${isDark ? 'text-white/70' : 'text-slate-600'}`}>{o}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}